// src/user/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { auth, firebaseApp } from '../firebase-app-core';
import { addSocialAuthDebug } from './socialAuthDebug';
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  PhoneAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getIdTokenResult,
  getAdditionalUserInfo,
  setPersistence,
  browserLocalPersistence,
  linkWithPopup,
  linkWithCredential,
  unlink,
} from 'firebase/auth';

let firestoreDepsPromise = null;

function loadFirestoreDeps() {
  if (!firestoreDepsPromise) {
    firestoreDepsPromise = import('../firebase-firestore-selected').then((fs) => ({
      db: fs.getFirestore(firebaseApp),
      doc: fs.doc,
      setDoc: fs.setDoc,
      serverTimestamp: fs.serverTimestamp,
      getDoc: fs.getDoc,
      onSnapshot: fs.onSnapshot,
    }));
  }
  return firestoreDepsPromise;
}

/* ---------- Context ---------- */
export const AuthContext = createContext({
  user: null,
  // `authLoading` is `true` until Firebase has fired its first
  // onAuthStateChanged callback. Without this, components that read
  // `user` on initial render (PrivateRoute, AppLive, BillingPanel,
  // …) treat the brief pre-resolve window as "logged out" and
  // either redirect to /login or render a "please log in" placeholder
  // — even for users who are actually signed in. Consumers should
  // gate guard logic on `if (authLoading) return <Spinner/>` and
  // ONLY redirect once `authLoading === false && !user`.
  authLoading: true,
  // 2026-05-19 — Drives PrivateRoute's "SMS auto-login in flight / failed"
  // surfaces. See the customToken effect in AuthProvider for the state
  // machine. Default 'idle' so consumers without a real provider don't
  // accidentally render the SMS-failure UI.
  smsAuthState: 'idle',
  setUser: () => {},
  signup: async () => {},
  login: async () => {},
  resendVerificationEmail: async () => {},
  loginWithGoogle: async () => {},
  loginWithApple: async () => {},
  socialRedirectResult: null,
  linkGoogleToAccount: async () => {},
  linkAppleToAccount: async () => {},
  linkEmailPasswordToAccount: async () => {},
  linkPhoneCredentialToAccount: async () => {},
  unlinkPhoneFromAccount: async () => {},
  logout: () => {},
  refreshUser: async () => {},
  _forceWelcomeCheck: async () => {},
});

/* ---------- helpers ---------- */
function deriveNameFromEmail(email = '') {
  const local = String(email).split('@')[0] || 'Friend';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ') || 'Friend';
}

function getProviderIds(fbUser) {
  return (fbUser?.providerData || [])
    .map((p) => String(p?.providerId || '').trim())
    .filter(Boolean);
}

function isPasswordAccount(fbUser) {
  return getProviderIds(fbUser).includes('password');
}

function canSendWelcome(fbUser) {
  if (!fbUser) return false;
  if (!isPasswordAccount(fbUser)) return true;
  return !!fbUser.emailVerified;
}

function getSocialProviderLabel(providerId = '') {
  const normalized = String(providerId || '').trim().toLowerCase();
  if (normalized === 'google.com') return 'Google';
  if (normalized === 'apple.com') return 'Apple';
  return 'Social';
}

function getSocialLoginCallToAction(providerId = '') {
  const normalized = String(providerId || '').trim().toLowerCase();
  if (normalized === 'google.com') {
    return 'Your account is connected to Google social login. Please use the Google button below.';
  }
  if (normalized === 'apple.com') {
    return 'Your account is connected to Apple social login. Please use the Apple button below.';
  }
  return 'Your account is connected to social login. Please use the social provider button below.';
}

function getEmailVerificationActionSettings() {
  const fallbackOrigin = 'https://lextrackai.com';
  const origin =
    (typeof window !== 'undefined' && window.location?.origin) || fallbackOrigin;
  const url = new URL('/login', origin);
  url.searchParams.set('verified', '1');
  url.searchParams.set('next', '/profile');
  return { url: url.toString() };
}

function hashIdempotencyKey(s = '') {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return String(h);
}

const SOCIAL_REDIRECT_FLOW_KEY = 'lex_social_redirect_flow';

function isMobileBrowser() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches;
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|CriOS/i.test(ua);
  return !!(mobileUa || coarsePointer);
}

function isIOSBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  return /iPhone|iPad|iPod/i.test(ua);
}

function isAuthDomainSameHost() {
  if (typeof window === 'undefined') return true;
  const authDomain = String(auth?.config?.authDomain || '').toLowerCase();
  const host = String(window.location.hostname || '').toLowerCase();
  return !!authDomain && authDomain === host;
}

function rememberSocialRedirectFlow(providerId, flow) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.setItem(
      SOCIAL_REDIRECT_FLOW_KEY,
      JSON.stringify({
        providerId,
        flow: flow === 'signup' ? 'signup' : 'login',
        at: Date.now(),
      })
    );
  } catch {}
}

function consumeSocialRedirectFlow() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage?.getItem(SOCIAL_REDIRECT_FLOW_KEY);
    window.sessionStorage?.removeItem(SOCIAL_REDIRECT_FLOW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.at || 0) > 10 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 统一 API 基址 */
function getApiBase() {
  const fromEnv = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.endsWith('lextrackai.com')) return 'https://api.lextrackai.com';
  }
  return '';
}
const API_BASE = getApiBase();

function getWelcomeEndpoints() {
  const endpoints = [];
  if (API_BASE) endpoints.push(`${API_BASE}/api/inmail/welcome`);
  endpoints.push('/api/inmail/welcome');
  return Array.from(new Set(endpoints.map((u) => String(u || '').trim()).filter(Boolean)));
}

function getVerifyEmailEndpoints() {
  const endpoints = [];
  if (API_BASE) endpoints.push(`${API_BASE}/api/auth/verify-email-request`);
  endpoints.push('/api/auth/verify-email-request');
  return Array.from(new Set(endpoints.map((u) => String(u || '').trim()).filter(Boolean)));
}

async function fetchWithTimeout(url, opts = {}, timeoutMs = 12000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function callServerWelcomeOnce({ idToken, name, email, uid }) {
  const headers = {
    'Content-Type': 'application/json',
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    'Idempotency-Key': hashIdempotencyKey(`${uid || email || ''}::welcome`),
  };
  const body = { ...(email ? { email } : {}), ...(name ? { name } : {}) };

  const endpoints = getWelcomeEndpoints();
  const errors = [];

  for (const endpoint of endpoints) {
    try {
      const res = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const ctype = res.headers.get('content-type') || '';
      // eslint-disable-next-line no-console
      console.log('[welcome] fetch url:', res.url, 'status:', res.status, 'ctype:', ctype);

      let json = null;
      if (ctype.includes('application/json')) {
        json = await res.json().catch(() => null);
      } else {
        const text = await res.text().catch(() => '');
        console.warn(
          '[welcome] non-JSON response preview:',
          (text || '').slice(0, 200),
          'endpoint=',
          endpoint
        );
      }

      if (res.status === 202) {
        return { status: 'in_progress', retryAfter: Number(res.headers.get('retry-after') || 10) || 10 };
      }

      if (!res.ok || !json || json?.error) {
        const err = new Error((json && json.error) || 'WELCOME_SEND_FAILED');
        err.status = res.status;
        err.payload = json;
        err.endpoint = endpoint;
        errors.push(err);
        continue;
      }

      return json; // 可能含 {ok:true} / {ok:true,dedup:true} / {dryRun:true}
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e || 'WELCOME_SEND_FAILED'));
      err.endpoint = endpoint;
      errors.push(err);
    }
  }

  const last = errors[errors.length - 1] || new Error('WELCOME_SEND_FAILED');
  throw last;
}

async function requestEmailVerificationViaServer(fbUser, explicitEmail = '', options = {}) {
  if (!fbUser) throw new Error('VERIFY_NO_AUTH_USER');
  const idToken = await fbUser.getIdToken();
  if (!idToken) throw new Error('VERIFY_NO_ID_TOKEN');

  const verifyAction = getEmailVerificationActionSettings();
  const email = String(explicitEmail || fbUser.email || '').trim().toLowerCase();
  const scope = String(options?.scope || 'verify-email');
  const forceFresh = !!options?.forceFresh;
  const nonce = forceFresh ? `:${Date.now()}:${Math.random().toString(36).slice(2, 8)}` : '';
  const endpoints = getVerifyEmailEndpoints();
  const errors = [];
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
    'Idempotency-Key': hashIdempotencyKey(`${fbUser.uid || email || ''}::${scope}${nonce}`),
  };
  const body = JSON.stringify({
    ...(email ? { email } : {}),
    continueUrl: verifyAction.url,
  });

  for (const endpoint of endpoints) {
    try {
      const res = await fetchWithTimeout(
        endpoint,
        {
          method: 'POST',
          headers,
          body,
        },
        12000
      );
      const ctype = res.headers.get('content-type') || '';
      const json = ctype.includes('application/json')
        ? await res.json().catch(() => null)
        : null;
      const retryAfterSeconds = Number(
        res.headers.get('retry-after') || json?.retryAfterSeconds || 0
      ) || 0;

      if (json?.throttled || res.status === 429) {
        return {
          ok: true,
          sent: false,
          throttled: true,
          retryAfterSeconds: retryAfterSeconds || 60,
        };
      }

      if (res.ok && (!json || json.ok !== false)) {
        const sent = (json?.delivered !== false) || !!json?.queued;
        return {
          ok: true,
          sent,
          throttled: false,
          queued: !!json?.queued,
          delivered: json?.delivered !== false,
          retryAfterSeconds,
        };
      }

      const err = new Error((json && json.error) || 'VERIFY_EMAIL_SEND_FAILED');
      err.status = res.status;
      err.payload = json;
      err.endpoint = endpoint;
      errors.push(err);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e || 'VERIFY_EMAIL_SEND_FAILED'));
      err.endpoint = endpoint;
      errors.push(err);
    }
  }

  const last = errors[errors.length - 1] || new Error('VERIFY_EMAIL_SEND_FAILED');
  throw last;
}

async function requestEmailVerificationBestEffort(
  fbUser,
  explicitEmail = '',
  options = {}
) {
  return requestEmailVerificationViaServer(fbUser, explicitEmail, options);
}

/* ---------- Provider ---------- */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [socialRedirectResult, setSocialRedirectResult] = useState(null);
  // True until Firebase Auth has resolved its persisted state on this
  // tab. Flips to false on the first onAuthStateChanged callback,
  // regardless of whether `fbUser` is null (signed-out) or set
  // (signed-in). PrivateRoute waits for this before redirecting,
  // which kills the "refresh /profile → flash to /login → bounce
  // back" white-screen flicker.
  const [authLoading, setAuthLoading] = useState(true);

  // 2026-05-19 — SMS short-link sign-in state. Drives the UX in
  // PrivateRoute and any page that wants to differentiate "I'm waiting
  // on an SMS auto-login" from "I'm just generically loading auth":
  //   'idle'       no ?lex_sms_auth= seen this tab
  //   'pending'    exchange in flight (signInWithCustomToken running)
  //   'succeeded'  exchange returned OK; the onAuthStateChanged effect
  //                will hydrate `user` shortly after
  //   'failed'     token expired / revoked / bad signature. PrivateRoute
  //                shows an inline "couldn't sign you in from SMS — try
  //                again" surface instead of bouncing to /login, so the
  //                user doesn't lose the destination URL.
  const [smsAuthState, setSmsAuthState] = useState('idle');

  // Per-session attempt guard
  const welcomeAttemptedForUidRef = useRef(new Set()); // Set<uid>
  const lastObservedUidRef = useRef(null);

  // Map<uid, { attempts:number, inflight:boolean, timer:any }>
  const welcomeStateRef = useRef(new Map());

  const MAX_ATTEMPTS = 6;
  const BACKOFF_STEPS_MS = [0, 2000, 5000, 15000, 30000, 60000];

  function clearRetryState(uid) {
    const st = welcomeStateRef.current.get(uid);
    if (st?.timer) clearTimeout(st.timer);
    welcomeStateRef.current.delete(uid);
    welcomeAttemptedForUidRef.current.delete(uid);
  }

  function scheduleRetry(uid, fbUser, snapshotData, delayMs) {
    const st = welcomeStateRef.current.get(uid) || { attempts: 0, inflight: false, timer: null };
    if (st.attempts >= MAX_ATTEMPTS) return;
    const delay = Math.max(0, delayMs ?? BACKOFF_STEPS_MS[Math.min(st.attempts, BACKOFF_STEPS_MS.length - 1)]);
    if (st.timer) clearTimeout(st.timer);
    st.timer = setTimeout(() => {
      welcomeAttemptedForUidRef.current.delete(uid);
      maybeTriggerWelcome(fbUser, snapshotData);
    }, delay);
    welcomeStateRef.current.set(uid, st);
  }

  async function ensureWelcomeSendNow(fbUser, snapshotData) {
    const uid = fbUser?.uid;
    if (!uid) return;

    const st = welcomeStateRef.current.get(uid) || { attempts: 0, inflight: false, timer: null };
    if (st.inflight) return;
    if (st.attempts >= MAX_ATTEMPTS) return;

    st.inflight = true;
    st.attempts += 1;
    welcomeStateRef.current.set(uid, st);

    try {
      const email = fbUser.email || snapshotData?.email || '';
      const displayName = fbUser.displayName || snapshotData?.displayName || deriveNameFromEmail(email);
      let idToken = '';
      try {
        idToken = await fbUser?.getIdToken?.();
      } catch {}
      if (!idToken) {
        try {
          idToken = await auth.currentUser?.getIdToken?.();
        } catch {}
      }
      const result = await callServerWelcomeOnce({ idToken, name: displayName, email, uid });

      // eslint-disable-next-line no-console
      console.log('[welcome] server result:', result);

      if (result?.dryRun) {
        welcomeAttemptedForUidRef.current.delete(uid);
        scheduleRetry(uid, fbUser, snapshotData, 5000);
      } else if (result?.status === 'in_progress') {
        // 服务端进行中，根据 Retry-After 退避
        welcomeAttemptedForUidRef.current.delete(uid);
        scheduleRetry(uid, fbUser, snapshotData, (result.retryAfter || 10) * 1000);
      } else if (result?.dedup) {
        clearRetryState(uid);
      }
      // 正常发送成功 -> Firestore 会写 welcomeSentAt；监听里会清理
    } catch (e) {
      console.warn('[welcome] server call failed:', e?.status, e?.payload || e?.message || e);
      welcomeAttemptedForUidRef.current.delete(uid);
      scheduleRetry(uid, fbUser, snapshotData); // 指数退避
    } finally {
      const cur = welcomeStateRef.current.get(uid);
      if (cur) {
        cur.inflight = false;
        welcomeStateRef.current.set(uid, cur);
      }
    }
  }

  // Gate
  const maybeTriggerWelcome = async (fbUser, snapshotData) => {
    if (!fbUser) return;
    const uid = fbUser.uid;
    if (!uid) return;
    if (!canSendWelcome(fbUser)) return;

    if (snapshotData?.welcomeSentAt) {
      clearRetryState(uid);
      return;
    }
    if (welcomeAttemptedForUidRef.current.has(uid)) return;

    welcomeAttemptedForUidRef.current.add(uid);
    await ensureWelcomeSendNow(fbUser, snapshotData);
  };

  /* ---------- SMS short-link auto-login (consume ?lex_sms_auth=) ----------
   *
   * 2026-05-18 — When a recipient taps an SMS short link of the form
   *   https://api.lextrackai.com/x/<code>
   * the backend (/x/:code, see shortlink-route.js) resolves the row, and
   * IF the row has a uid attached, it mints a Firebase Custom Token and
   * appends it to the redirect:
   *   https://lextrackai.com/app?lexai=phone&lex_sms_auth=<token>&lex_sms_attr=<code>
   *
   * Goal: the recipient — who already verified their phone with Firebase
   * Auth when they signed up — should land INSIDE their session, not get
   * bounced to a phone-number prompt. We do that by:
   *   1. Reading the param off `window.location` ASAP (before anything
   *      reads `user` and decides to redirect to /login).
   *   2. Calling signInWithCustomToken() — this triggers the normal
   *      onAuthStateChanged flow below, which populates `user` and
   *      flips `authLoading=false` like any other sign-in.
   *   3. Stripping the param from the URL via history.replaceState so
   *      a refresh / share doesn't leak the custom token in the URL bar.
   *
   * Failure modes are non-fatal:
   *   - bad/expired token → signInWithCustomToken throws, we log + strip
   *     the param + let the user fall through to the regular auth flow
   *     (which will show the login wall, same as before this fix).
   *   - no window (SSR build path) → noop, the effect doesn't run.
   *
   * NOTE: this effect MUST be declared BEFORE the onAuthStateChanged
   * effect so the React effect ordering kicks off the token exchange
   * before any consumer (PrivateRoute, etc.) reads the initial auth
   * state. In practice authLoading stays `true` across the exchange
   * (the existing onAuthStateChanged effect only flips it on the first
   * callback), so PrivateRoute won't ever redirect mid-exchange.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let token = '';
    try {
      const params = new URLSearchParams(window.location.search || '');
      token = String(params.get('lex_sms_auth') || '').trim();
    } catch {
      token = '';
    }
    if (!token) return;

    // 2026-05-19 — Flip to 'pending' synchronously so PrivateRoute (or
    // any other gate that mounts before the await below resolves) can
    // tell the difference between "SMS auto-login in flight, please
    // wait" and "generic anonymous user, bounce to /login".
    setSmsAuthState('pending');

    // Strip the token from the visible URL right away so it doesn't sit
    // in the address bar / get bookmarked / get copy-pasted. We keep
    // `lex_sms_attr` (the short code) so destination pages that want to
    // know "which SMS sent the user here" can still read it. The token
    // is already captured in the local `token` variable above so the
    // async exchange below still works after the strip.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('lex_sms_auth');
      const cleaned = url.pathname + (url.search || '') + (url.hash || '');
      window.history.replaceState(window.history.state, '', cleaned);
    } catch (e) {
      console.warn('[auth] failed to strip lex_sms_auth from URL:', e?.message || e);
    }

    (async () => {
      try {
        await signInWithCustomToken(auth, token);
        // eslint-disable-next-line no-console
        console.log('[auth] SMS short-link custom-token sign-in succeeded.');
        setSmsAuthState('succeeded');
      } catch (e) {
        // Token expired (custom tokens are ~1h) / revoked / network /
        // bad signature → PrivateRoute will read smsAuthState='failed'
        // and render an inline retry surface instead of hard-bouncing
        // the user to /login (which loses the destination URL). For
        // pages that aren't behind PrivateRoute the warning is enough.
        console.warn(
          '[auth] SMS short-link custom-token sign-in failed:',
          e?.code || '',
          e?.message || e
        );
        setSmsAuthState('failed');
      }
    })();
  }, []);

  /* ---------- Sync auth + Firestore user doc (real-time) ---------- */
  //
  // 2026-05-07 (CRITICAL FIX): Previously this effect flipped
  // `authLoading=false` synchronously inside the onAuthStateChanged
  // callback, BEFORE the Firestore user-doc snapshot returned. That
  // opened a race window where consumers saw `authLoading=false` AND
  // `user=null` at the same time, even for a perfectly valid logged-in
  // user. Two bad outcomes followed:
  //   1) PrivateRoute redirected logged-in users to /login (they got
  //      bounced right back, but it caused a visible flicker AND
  //      sometimes triggered the destination route to mount with a
  //      partial user object on the next render).
  //   2) Non-protected post-login destinations (/app, /profile via
  //      ?welcome=1, etc.) rendered with `user=null`. Any component
  //      that read `user.something.deeplyNested` (without optional
  //      chaining) threw — and the global ErrorBoundary showed
  //      "Something went wrong" instead of the page they expected.
  //
  // The fix: keep `authLoading=true` until BOTH the Firebase auth
  // state AND the React `user` state are finalized — i.e. until we've
  // seen the first snapshot of the user doc (or definitively know the
  // user is signed out). A 5 s safety-net timeout still flips
  // `authLoading=false` with an auth-only fallback user object in case
  // Firestore is unreachable, so the UI can never hang permanently.
  useEffect(() => {
    let unsubscribeUserDoc = null;
    let firstResolveTimeout = null;

    const buildAuthOnlyUser = (fbUser) => ({
      uid: fbUser.uid,
      email: fbUser.email,
      emailVerified: !!fbUser.emailVerified,
      providerIds: getProviderIds(fbUser),
      iconName: '',
      builderConfig: { base: 'Body_010', accessories: [] },
      role: 'viewer',
      subscriptions: {},
      stripe: {},
    });

    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser) => {
      // Cancel any pending resolve timer from the previous auth state
      // before starting a new one — uid changes mid-session (unlikely
      // but possible) shouldn't leave a stale timeout firing.
      if (firstResolveTimeout) {
        clearTimeout(firstResolveTimeout);
        firstResolveTimeout = null;
      }

      if (fbUser) {
        let fs = null;
        try {
          fs = await loadFirestoreDeps();
        } catch (e) {
          console.warn('[auth] failed to load Firestore deps:', e?.message || e);
          setUser(buildAuthOnlyUser(fbUser));
          setAuthLoading(false);
          return;
        }
        const { db, doc, onSnapshot } = fs;
        const userRef = doc(db, 'users', fbUser.uid);
        let tokenClaims = {};
        try {
          const tokenResult = await getIdTokenResult(fbUser, true);
          tokenClaims = tokenResult?.claims || {};
        } catch (e) {
          tokenClaims = {};
        }

        if (lastObservedUidRef.current && lastObservedUidRef.current !== fbUser.uid) {
          clearRetryState(lastObservedUidRef.current);
        }
        lastObservedUidRef.current = fbUser.uid;

        let firstResolved = false;
        const resolveOnce = (mergedUser) => {
          if (firstResolved) {
            // Subsequent snapshots only need to update `user`; authLoading
            // is already off.
            setUser(mergedUser);
            return;
          }
          firstResolved = true;
          if (firstResolveTimeout) {
            clearTimeout(firstResolveTimeout);
            firstResolveTimeout = null;
          }
          setUser(mergedUser);
          setAuthLoading(false);
        };

        // Safety net: if Firestore is offline / rules block the read /
        // SDK is mid-init, don't hang the app forever. After 5 s,
        // populate `user` from auth alone and unblock the UI.
        firstResolveTimeout = setTimeout(() => {
          if (firstResolved) return;
          console.warn('[auth] Firestore user-doc snapshot timed out after 5s; falling back to auth-only user.');
          resolveOnce(buildAuthOnlyUser(fbUser));
        }, 5000);

        // 监听用户文档
        unsubscribeUserDoc = onSnapshot(
          userRef,
          async (snap) => {
            const data = snap.exists() ? snap.data() : {};
            const providerIds = getProviderIds(fbUser);

            const merged = {
              uid: fbUser.uid,
              email: fbUser.email,
              ...data,
              emailVerified: !!fbUser.emailVerified,
              providerIds,
              iconName: data.iconName || '',
              builderConfig: data.builderConfig || { base: 'Body_010', accessories: [] },
              role: data.role || 'viewer',
              subscriptions: data.subscriptions || {},
              stripe: data.stripe || {},
              claims: tokenClaims,
              superadmin: data.superadmin === true || tokenClaims.superadmin === true,
              superAdmin: data.superAdmin === true || tokenClaims.superAdmin === true,
              admin: data.admin === true || tokenClaims.admin === true,
            };
            resolveOnce(merged);

            if (!data?.welcomeSentAt) {
              maybeTriggerWelcome(fbUser, data);
              // 轻柔二次触发，避免启动竞态
              setTimeout(() => {
                if (!welcomeStateRef.current.get(fbUser.uid)?.inflight && !data?.welcomeSentAt) {
                  welcomeAttemptedForUidRef.current.delete(fbUser.uid);
                  maybeTriggerWelcome(fbUser, data);
                }
              }, 3000);
            } else {
              clearRetryState(fbUser.uid);
            }
          },
          (err) => {
            console.warn('Failed to snapshot user doc:', err);
            // Snapshot failed (permission denied, network, …). Don't
            // leave the UI stuck in `authLoading`; resolve with an
            // auth-only fallback so PrivateRoute / pages can move on.
            if (!firstResolved) {
              resolveOnce(buildAuthOnlyUser(fbUser));
            }
          }
        );
      } else {
        // Logged out: flip user AND authLoading together so consumers
        // never observe (authLoading=false, user=null-but-actually-
        // signed-in) — that combination should now ALWAYS mean "really
        // signed out".
        setUser(null);
        setAuthLoading(false);
        if (unsubscribeUserDoc) {
          unsubscribeUserDoc();
          unsubscribeUserDoc = null;
        }
        for (const [uid] of welcomeStateRef.current.entries()) clearRetryState(uid);
        welcomeStateRef.current.clear();
        welcomeAttemptedForUidRef.current.clear();
        lastObservedUidRef.current = null;
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      if (firstResolveTimeout) {
        clearTimeout(firstResolveTimeout);
        firstResolveTimeout = null;
      }
      for (const [uid] of welcomeStateRef.current.entries()) clearRetryState(uid);
      welcomeStateRef.current.clear();
      welcomeAttemptedForUidRef.current.clear();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (e) {
        console.warn('[auth] failed to set browser local persistence:', e?.message || e);
      }

      const rememberedFlow = consumeSocialRedirectFlow();
      addSocialAuthDebug('redirect-result:check:start', {
        rememberedFlow: rememberedFlow || null,
      });
      try {
        const result = await getRedirectResult(auth);
        addSocialAuthDebug('redirect-result:check:done', {
          hasResult: !!result,
          uid: result?.user?.uid || null,
          providerId: result?.providerId || rememberedFlow?.providerId || '',
        });
        if (cancelled || !result?.user) return;
        const providerId =
          result.providerId ||
          rememberedFlow?.providerId ||
          getProviderIds(result.user).find((id) => id === 'google.com' || id === 'apple.com') ||
          'google.com';
        const additional = getAdditionalUserInfo(result);
        addSocialAuthDebug('redirect-result:user:received', {
          providerId,
          flow: rememberedFlow?.flow || 'login',
          uid: result.user.uid,
          isNewUser: !!additional?.isNewUser,
        });
        await ensureSocialUserDoc(result.user, providerId);
        triggerSocialWelcomeInBackground(result.user, providerId);
        setSocialRedirectResult({
          providerId,
          flow: rememberedFlow?.flow || 'login',
          isNewUser: !!additional?.isNewUser,
          uid: result.user.uid,
        });
      } catch (e) {
        if (!cancelled) {
          console.warn('[auth] social redirect result failed:', e?.code || '', e?.message || e);
          addSocialAuthDebug('redirect-result:error', {
            code: e?.code || '',
            message: e?.message || String(e),
            rememberedFlow: rememberedFlow || null,
          });
          setSocialRedirectResult({
            error: true,
            code: e?.code || '',
            message: e?.message || 'Social sign-in did not complete.',
            flow: rememberedFlow?.flow || 'login',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- Helpers ---------- */
  const refreshUser = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    try {
      try {
        await fbUser.reload();
      } catch (reloadErr) {
        console.warn('[auth] refreshUser reload failed:', reloadErr?.message || reloadErr);
      }
      const activeUser = auth.currentUser || fbUser;
      const authOnlyUser = {
        uid: activeUser.uid,
        email: activeUser.email,
        emailVerified: !!activeUser.emailVerified,
        providerIds: getProviderIds(activeUser),
        iconName: '',
        builderConfig: { base: 'Body_010', accessories: [] },
        role: 'viewer',
        subscriptions: {},
        stripe: {},
      };

      // Force React context to catch up immediately after popup auth.
      // Chrome iOS can have auth.currentUser populated while our
      // onAuthStateChanged/onSnapshot path has not hydrated `user` yet.
      setUser(authOnlyUser);

      const { db, doc, getDoc } = await loadFirestoreDeps();
      const userRef = doc(db, 'users', activeUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      const providerIds = getProviderIds(activeUser);

      setUser({
        uid: activeUser.uid,
        email: activeUser.email,
        ...data,
        emailVerified: !!activeUser.emailVerified,
        providerIds,
        iconName: data.iconName || '',
        builderConfig: { base: 'Body_010', accessories: [] },
        role: data.role || 'viewer',
        subscriptions: data.subscriptions || {},
        stripe: data.stripe || {},
      });

      if (!data?.welcomeSentAt) {
        welcomeAttemptedForUidRef.current.delete(activeUser.uid);
        maybeTriggerWelcome(activeUser, data);
      }
    } catch (e) {
      console.warn('refreshUser failed:', e);
    }
  };

  /* ---------- Email / Password ---------- */
  const signup = async ({ email, password }) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    try {
      const cred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      let verificationEmailSent = false;
      try {
        const verifyResult = await requestEmailVerificationBestEffort(
          cred.user,
          normalizedEmail
        );
        verificationEmailSent = !!verifyResult?.sent || !!verifyResult?.throttled;
      } catch (verifyErr) {
        console.warn('[auth] signup verify-email send failed:', verifyErr?.message || verifyErr);
      }

      // 2026-05-06 (P0-1): wait for Firebase client SDK to populate auth
      // state before doing the first Firestore write. Without this, the
      // setDoc fires while `auth.currentUser` is still null and the new
      // Firestore rules `allow write: if request.auth != null` reject it.
      // Two-phase wait: prefer onAuthStateChanged matching the new uid;
      // fall back to a token refresh; cap total wait at 5 s so we don't
      // hang signup on a flaky network.
      try {
        await new Promise((resolve, reject) => {
          if (auth.currentUser?.uid === cred.user.uid) return resolve();
          const timer = setTimeout(() => {
            unsub?.();
            reject(new Error('auth-state-timeout'));
          }, 5000);
          const unsub = auth.onAuthStateChanged((u) => {
            if (u && u.uid === cred.user.uid) {
              clearTimeout(timer);
              unsub();
              resolve();
            }
          });
        });
      } catch {
        // best-effort second nudge: force a token refresh which usually
        // synchronizes the Firestore client-side auth state.
        try { await cred.user.getIdToken(true); } catch {}
      }

      const { db, doc, setDoc, serverTimestamp } = await loadFirestoreDeps();
      await setDoc(doc(db, 'users', cred.user.uid), {
        email: normalizedEmail,
        createdAt: serverTimestamp(),
        builderConfig: { base: 'Body_010', accessories: [] },
        role: 'viewer',
        emailVerified: false,
        verificationEmailSentAt: verificationEmailSent ? serverTimestamp() : null,
      });
      return {
        ...cred,
        email: normalizedEmail,
        needsEmailVerification: true,
        verificationEmailSent,
      };
    } catch (e) {
      const code = String(e?.code || '').toLowerCase();
      if (code !== 'auth/email-already-in-use') throw e;

      // Provider-aware branch for existing accounts so social-only users
      // get the correct call-to-action instead of a generic "email exists".
      try {
        const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
        const methodList = Array.isArray(methods) ? methods : [];
        const hasPassword = methodList.includes('password');
        const socialProviderId = methodList.find((m) => m && m !== 'password');
        if (socialProviderId && !hasPassword) {
          const err = new Error(getSocialLoginCallToAction(socialProviderId));
          err.code = 'auth/account-exists-with-different-credential';
          throw err;
        }
      } catch (methodErr) {
        const methodCode = String(methodErr?.code || '').toLowerCase();
        if (methodCode === 'auth/account-exists-with-different-credential') {
          throw methodErr;
        }
        console.warn('[auth] signup provider lookup failed:', methodErr?.message || methodErr);
      }

      // Existing account flow: if it's an unverified password account, resend verification.
      let existingCred = null;
      try {
        existingCred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      } catch (signInErr) {
        const signInCode = String(signInErr?.code || '').toLowerCase();
        if (signInCode === 'auth/wrong-password' || signInCode === 'auth/invalid-credential') {
          const err = new Error('Email already exists. Use your existing password to log in.');
          err.code = 'auth/email-already-in-use';
          throw err;
        }
        throw signInErr;
      }

      const existingUser = existingCred?.user;
      if (!existingUser) {
        const err = new Error('Email already in use. Please log in.');
        err.code = 'auth/email-already-in-use';
        throw err;
      }

      const providerIds = getProviderIds(existingUser);
      if (!providerIds.includes('password')) {
        await signOut(auth);
        const socialProviderId =
          providerIds.find((providerId) => providerId && providerId !== 'password') || '';
        const err = new Error(getSocialLoginCallToAction(socialProviderId));
        err.code = 'auth/account-exists-with-different-credential';
        throw err;
      }

      if (existingUser.emailVerified) {
        await signOut(auth);
        const err = new Error('Email already exists. Please log in.');
        err.code = 'auth/email-already-in-use';
        throw err;
      }

      let verificationEmailSent = false;
      try {
        const verifyResult = await requestEmailVerificationBestEffort(
          existingUser,
          normalizedEmail,
          { scope: 'verify-email-signup-resend', forceFresh: true }
        );
        verificationEmailSent = !!verifyResult?.sent || !!verifyResult?.throttled;
      } catch (verifyErr) {
        console.warn('[auth] signup existing-user verify-email send failed:', verifyErr?.message || verifyErr);
      }

      return {
        user: existingUser,
        email: normalizedEmail,
        needsEmailVerification: true,
        verificationEmailSent,
        existingAccount: true,
      };
    }
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const fbUser = cred?.user;
    if (isPasswordAccount(fbUser) && !fbUser?.emailVerified) {
      let verifyResult = { ok: false, sent: false, throttled: false };
      try {
        verifyResult = await requestEmailVerificationBestEffort(
          fbUser,
          email,
          { scope: 'verify-email-login-resend', forceFresh: true }
        );
      } catch (verifyErr) {
        console.warn('[auth] login verify-email resend failed:', verifyErr?.message || verifyErr);
      }
      const verificationEmailSent =
        !!verifyResult?.sent || !!verifyResult?.throttled;
      await signOut(auth);
      const retryAfter = Number(verifyResult?.retryAfterSeconds || 0) || 0;
      let message = 'Please verify your email first. We could not send a verification email right now. Please try again shortly.';
      if (verifyResult?.throttled && retryAfter > 0) {
        message = `Please verify your email first. Verification email was sent recently. Try again in ${retryAfter}s.`;
      } else if (verificationEmailSent) {
        message = 'Please verify your email first. A verification link has been sent to your inbox.';
      }
      const err = new Error(message);
      err.code = 'auth/email-not-verified';
      if (verifyResult?.throttled) err.retryAfterSeconds = retryAfter || 60;
      throw err;
    }
    return cred;
  };

  const resendVerificationEmail = async (email = '', options = {}) => {
    const fbUser = auth.currentUser;
    if (!fbUser) {
      return { ok: false, sent: false, throttled: false, error: 'AUTH_REQUIRED' };
    }
    if (fbUser.emailVerified) {
      return { ok: true, sent: false, throttled: false, alreadyVerified: true };
    }
    const normalizedEmail = String(email || fbUser.email || '').trim().toLowerCase();
    try {
      return await requestEmailVerificationBestEffort(
        fbUser,
        normalizedEmail,
        { scope: 'verify-email-manual-resend', forceFresh: true, ...options }
      );
    } catch (err) {
      console.warn('[auth] manual resend verify-email failed:', err?.message || err);
      return { ok: false, sent: false, throttled: false };
    }
  };

  /* ---------- Google / Apple ---------- */
  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider('apple.com');
  appleProvider.addScope('email');
  appleProvider.addScope('name');

  // 2026-05-07: split the bootstrap into two phases.
  //   Phase 1 (await): if this is a brand-new social account, create
  //     the Firestore user doc BEFORE returning to the caller. The
  //     destination page (e.g. ProfilePanel) reads many user fields,
  //     and previously the doc could still be missing when the page
  //     first rendered → empty state / wrong copy / silent broken
  //     queries.
  //   Phase 2 (fire-and-forget): welcome email round-trip stays in
  //     the background — it can take 1-3s on a cold backend and
  //     blocking on it would visibly delay the post-popup transition.
  const ensureSocialUserDoc = async (fbUser, providerId = '') => {
    const providerLabel = getSocialProviderLabel(providerId);
    if (!fbUser?.uid) return;
    try {
      const { db, doc, getDoc, setDoc, serverTimestamp } = await loadFirestoreDeps();
      const userRef = doc(db, 'users', fbUser.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          email: fbUser.email,
          createdAt: serverTimestamp(),
          builderConfig: { base: 'Body_010', accessories: [] },
          role: 'viewer',
        });
      }
    } catch (e) {
      console.warn(
        `[auth] ${providerLabel.toLowerCase()} user-doc bootstrap failed:`,
        e?.message || e
      );
      // Non-fatal: AuthContext's onSnapshot will pick up an empty
      // doc and merge fbUser fields. Pages should still render.
    }
  };

  const triggerSocialWelcomeInBackground = (fbUser, providerId = '') => {
    const providerLabel = getSocialProviderLabel(providerId);
    if (!fbUser?.uid) return;
    (async () => {
      const seed = {
        email: fbUser.email || '',
        displayName: fbUser.displayName || deriveNameFromEmail(fbUser.email || ''),
      };
      welcomeAttemptedForUidRef.current.delete(fbUser.uid);
      try {
        await ensureWelcomeSendNow(fbUser, seed);
      } catch (e) {
        console.warn(
          `[welcome] immediate ${providerLabel.toLowerCase()} trigger failed:`,
          e?.message || e
        );
      }
      setTimeout(() => {
        welcomeAttemptedForUidRef.current.delete(fbUser.uid);
        Promise.resolve(maybeTriggerWelcome(fbUser, seed)).catch(() => {});
      }, 2000);
    })();
  };

  const startSocialAuth = async (provider, providerId, options = {}) => {
    const flow = options?.flow === 'signup' ? 'signup' : 'login';
    const useRedirect = isMobileBrowser() && !(isIOSBrowser() && !isAuthDomainSameHost());
    addSocialAuthDebug('social-auth:start', {
      providerId,
      flow,
      mobile: isMobileBrowser(),
      ios: isIOSBrowser(),
      authDomainSameHost: isAuthDomainSameHost(),
      strategy: useRedirect ? 'redirect' : 'popup',
    });
    try {
      await setPersistence(auth, browserLocalPersistence);
      addSocialAuthDebug('social-auth:persistence:ok', { providerId, flow });
    } catch (e) {
      console.warn('[auth] failed to set social auth persistence:', e?.message || e);
      addSocialAuthDebug('social-auth:persistence:error', {
        providerId,
        flow,
        message: e?.message || String(e),
      });
    }
    if (useRedirect) {
      rememberSocialRedirectFlow(providerId, flow);
      addSocialAuthDebug('social-auth:redirect:start', { providerId, flow });
      await signInWithRedirect(auth, provider);
      return { redirectStarted: true, providerId, flow };
    }
    addSocialAuthDebug('social-auth:popup:start', { providerId, flow });
    const result = await signInWithPopup(auth, provider);
    const fbUser = result.user;
    const additional = getAdditionalUserInfo(result);
    addSocialAuthDebug('social-auth:popup:ok', {
      providerId,
      flow,
      uid: fbUser?.uid || null,
      isNewUser: !!additional?.isNewUser,
    });
    await ensureSocialUserDoc(fbUser, providerId);
    triggerSocialWelcomeInBackground(fbUser, providerId);
    return result;
  };

  const loginWithGoogle = async (options = {}) => {
    return startSocialAuth(googleProvider, 'google.com', options);
  };

  const loginWithApple = async (options = {}) => {
    return startSocialAuth(appleProvider, 'apple.com', options);
  };

  /* ---------- Link extra providers to current account ----------
     Used by Profile → Email row when a phone-only user wants to add
     an email by linking Google / Apple, or by creating an email+password
     credential. After linking, the user's `email` and `emailVerified`
     fields update on Firebase Auth, and we mirror `email` to Firestore. */

  const translateLinkError = (err, providerLabel) => {
    const code = String(err?.code || '').toLowerCase();
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      const out = new Error('CANCELLED');
      out.code = 'cancelled';
      out.silent = true;
      return out;
    }
    if (code === 'auth/popup-blocked') {
      const out = new Error(
        `Your browser blocked the ${providerLabel} popup. Please allow popups for this site and try again.`
      );
      out.code = 'popup-blocked';
      return out;
    }
    if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
      const out = new Error(
        `This ${providerLabel} account is already linked to another LexTrack account. Sign in with that account, or pick a different ${providerLabel} account.`
      );
      out.code = 'already-in-use';
      return out;
    }
    if (code === 'auth/provider-already-linked') {
      const out = new Error(`${providerLabel} is already linked to this account.`);
      out.code = 'already-linked';
      return out;
    }
    if (code === 'auth/requires-recent-login') {
      const out = new Error(
        'For security, please sign in again before adding a new login method.'
      );
      out.code = 'requires-recent-login';
      return out;
    }
    if (code === 'auth/weak-password') {
      const out = new Error('Password is too weak. Use at least 6 characters.');
      out.code = 'weak-password';
      return out;
    }
    if (code === 'auth/invalid-email') {
      const out = new Error('That email address looks invalid.');
      out.code = 'invalid-email';
      return out;
    }
    const out = new Error(err?.message || `Could not link ${providerLabel}.`);
    out.code = code || 'unknown';
    return out;
  };

  const syncEmailToFirestore = async (fbUser) => {
    if (!fbUser?.uid) return;
    const email = String(fbUser?.email || '').trim().toLowerCase();
    if (!email) return;
    try {
      const { db, doc, setDoc, serverTimestamp } = await loadFirestoreDeps();
      await setDoc(
        doc(db, 'users', fbUser.uid),
        {
          email,
          emailVerified: !!fbUser?.emailVerified,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('[auth] syncEmailToFirestore failed:', e?.message || e);
    }
  };

  const linkGoogleToAccount = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in.');
    try {
      const result = await linkWithPopup(fbUser, googleProvider);
      const linkedUser = result.user || fbUser;
      await syncEmailToFirestore(linkedUser);
      try { await refreshUser(); } catch {}
      return { ok: true, providerId: 'google.com', email: linkedUser?.email || '' };
    } catch (err) {
      throw translateLinkError(err, 'Google');
    }
  };

  const linkAppleToAccount = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in.');
    try {
      const result = await linkWithPopup(fbUser, appleProvider);
      const linkedUser = result.user || fbUser;
      await syncEmailToFirestore(linkedUser);
      try { await refreshUser(); } catch {}
      return { ok: true, providerId: 'apple.com', email: linkedUser?.email || '' };
    } catch (err) {
      throw translateLinkError(err, 'Apple');
    }
  };

  /**
   * Link a phone credential (already produced from a verified OTP) to the
   * current account. Caller is responsible for the OTP UI (RecaptchaVerifier
   * + PhoneAuthProvider.verifyPhoneNumber + PhoneAuthProvider.credential).
   * Pass the resulting credential here. We do the link + Firestore mirror.
   */
  const linkPhoneCredentialToAccount = async ({ credential, phoneE164, dialCode, phoneNumber } = {}) => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in.');
    if (!credential) {
      const out = new Error('Missing phone credential.');
      out.code = 'invalid-credential';
      throw out;
    }
    let linkedUser;
    try {
      const result = await linkWithCredential(fbUser, credential);
      linkedUser = result.user || fbUser;
    } catch (err) {
      throw translateLinkError(err, 'Phone');
    }
    // Mirror phone fields to Firestore so ProfilePanel / backend reads see them.
    try {
      const { db, doc, setDoc, serverTimestamp } = await loadFirestoreDeps();
      await setDoc(
        doc(db, 'users', linkedUser.uid),
        {
          phoneE164: phoneE164 || linkedUser?.phoneNumber || '',
          phoneNumber: String(phoneNumber || '').replace(/[^\d]/g, ''),
          dialCode: dialCode || '',
          countryDialCode: dialCode || '',
          phoneVerified: true,
          phoneVerifiedAt: serverTimestamp(),
          phoneVerificationPending: false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('[auth] linkPhone Firestore mirror failed:', e?.message || e);
    }
    try { await refreshUser(); } catch {}
    return { ok: true, providerId: 'phone', phoneE164: linkedUser?.phoneNumber || phoneE164 };
  };

  /**
   * Unlink the phone provider from the current account and clear the phone
   * fields in Firestore. Refuses if `phone` is the only remaining sign-in
   * method (would orphan the account). For accounts whose phone is only in
   * Firestore (never linked to Firebase Auth), skips the unlink and only
   * clears Firestore.
   */
  const unlinkPhoneFromAccount = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in.');

    const providerData = Array.isArray(fbUser.providerData) ? fbUser.providerData : [];
    const providerIds = providerData
      .map((p) => String(p?.providerId || '').trim())
      .filter(Boolean);
    const hasPhoneOnAuth = providerIds.includes('phone');
    const otherProviders = providerIds.filter((p) => p !== 'phone');

    if (hasPhoneOnAuth && otherProviders.length === 0) {
      const err = new Error(
        'Phone is your only sign-in method. Add an email (Google, Apple, or password) first, then remove the phone number.'
      );
      err.code = 'sole-sign-in-method';
      throw err;
    }

    if (hasPhoneOnAuth) {
      try {
        await unlink(fbUser, 'phone');
      } catch (err) {
        throw translateLinkError(err, 'Phone');
      }
    }

    // Always clear Firestore fields, regardless of where phone lived.
    try {
      const { db, doc, setDoc, serverTimestamp } = await loadFirestoreDeps();
      await setDoc(
        doc(db, 'users', fbUser.uid),
        {
          phoneE164: '',
          phoneNumber: '',
          dialCode: '',
          countryDialCode: '',
          countryIso2: '',
          phoneVerified: false,
          phoneVerifiedAt: null,
          phoneVerificationPending: false,
          welcomeSmsSentAt: null,
          welcomeSmsSentToPhone: '',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn('[auth] unlinkPhone Firestore clear failed:', e?.message || e);
    }
    try { await refreshUser(); } catch {}
    return { ok: true };
  };

  const linkEmailPasswordToAccount = async ({ email, password } = {}) => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in.');
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      const out = new Error('Please enter an email address.');
      out.code = 'invalid-email';
      throw out;
    }
    if (!password || String(password).length < 6) {
      const out = new Error('Password must be at least 6 characters.');
      out.code = 'weak-password';
      throw out;
    }
    let linkedUser;
    try {
      const credential = EmailAuthProvider.credential(normalizedEmail, password);
      const result = await linkWithCredential(fbUser, credential);
      linkedUser = result.user || fbUser;
    } catch (err) {
      throw translateLinkError(err, 'Email');
    }
    await syncEmailToFirestore(linkedUser);

    // Send the same verification email used at signup (best-effort, non-fatal).
    let verificationEmailSent = false;
    try {
      const verifyResult = await requestEmailVerificationBestEffort(
        linkedUser,
        normalizedEmail,
        { scope: 'verify-email-link-resend', forceFresh: true }
      );
      verificationEmailSent = !!verifyResult?.sent || !!verifyResult?.queued;
    } catch (verifyErr) {
      console.warn('[auth] post-link verify-email send failed:', verifyErr?.message || verifyErr);
    }
    try { await refreshUser(); } catch {}
    return {
      ok: true,
      providerId: 'password',
      email: linkedUser?.email || normalizedEmail,
      verificationEmailSent,
    };
  };

  /* ---------- Logout ---------- */
  // 2026-05-07 (CRITICAL FIX, supersedes 2026-05-06 B3): the previous
  // implementation called Firestore's `terminate(db)` + `clearIndexedDb
  // Persistence(db)` to wipe the offline cache. That's correct *if* the
  // user is going to reload the page right after logout — but in this
  // SPA they often don't (TopMenuBar.handleLogout just navigates to
  // "/"). After `terminate()` the `db` module-level singleton is
  // permanently dead, and the very next signup or login attempt blows
  // up with `FirebaseError: The client has already been terminated.`
  // because every Firestore call (onSnapshot user doc, setDoc users/...,
  // etc.) reuses that same dead instance.
  //
  // Fix: stop calling terminate() inline. Instead:
  //   1) Sign out of Firebase Auth (clears ID token from memory + IDB).
  //   2) Clear sessionStorage and the per-user localStorage keys we know
  //      about (preserves harmless UI prefs like theme).
  //   3) Defer the IndexedDB cache wipe to "next page load" by setting a
  //      flag in localStorage that the AppShell consults at boot. This
  //      way the cache *is* wiped, but only at a point where killing the
  //      `db` singleton is safe (because it'll be re-created by the
  //      module re-evaluation on the next full-page navigation / refresh).
  // The shared-device leak we were originally guarding against still
  // gets handled — just one full-page transition later than before.
  const PURGE_LOCAL_KEYS = [
    'lex_uid',                  // see src/Lexappstore/prebuild-cards/instagram.jsx
    'lex_spotlight_quota_v1',   // see SpotlightSwipeDeck — per-user
    'partnerForm',              // form drafts
    'MEMBERSHIP_LAST_INTENT_KEY',
    'lex:delete-verified-token',
    'lex:delete-verified-uid',
    'lex:delete-email-pending',
    'lex:delete-email-pending-until-ms',
    'lex:auth-return-scroll',
    'lex:eb:lastReloadAt',
  ];
  const FIRESTORE_WIPE_FLAG_KEY = 'lex:firestore-wipe-on-next-boot';
  const logout = async () => {
    try {
      // Clear app-level caches BEFORE signOut so anything subscribing to
      // auth-state-changed sees a clean slate.
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.clear();
          for (const k of PURGE_LOCAL_KEYS) {
            try { window.localStorage.removeItem(k); } catch {}
          }
          // Tell the next full-page load to wipe Firestore's IndexedDB
          // cache. We can't safely do it now without killing the `db`
          // singleton this SPA still relies on.
          try { window.localStorage.setItem(FIRESTORE_WIPE_FLAG_KEY, '1'); } catch {}
        }
      } catch (e) {
        console.warn('[logout] sessionStorage clear failed:', e);
      }
    } finally {
      await signOut(auth);
    }
  };

  /* ---------- Debug: manual kick ---------- */
  const _forceWelcomeCheck = async () => {
    const fbUser = auth.currentUser;
    if (!fbUser) return;
    try {
      const { db, doc, getDoc } = await loadFirestoreDeps();
      const userRef = doc(db, 'users', fbUser.uid);
      const snap = await getDoc(userRef);
      const data = snap.exists() ? snap.data() : {};
      welcomeAttemptedForUidRef.current.delete(fbUser.uid);
      await maybeTriggerWelcome(fbUser, data);
    } catch (e) {
      console.warn('_forceWelcomeCheck failed:', e?.message || e);
    }
  };

  /* ---------- Context Value ----------
   *
   * ⚠️ Performance critical. Without useMemo, this object is freshly
   * created on every render of AuthProvider, which forces every component
   * subscribed via useAuth() to re-render — that's basically every
   * authenticated screen in the app (ProfilePanel, ChatApp, AppLive,
   * AppStore, …). With useMemo + a stable deps list, useAuth consumers
   * only re-render when something they actually depend on changes.
   *
   * Note: action functions (signup, login, …) are reference-stable enough
   * for the deps list because they're declared inside AuthProvider but
   * don't close over reactive state in a way that would change them
   * between renders. If a future change introduces an unstable
   * dependency, wrap that action in useCallback first.
   */
  const value = useMemo(
    () => ({
      user,
      authLoading,
      smsAuthState,
      setUser,
      signup,
      login,
      resendVerificationEmail,
      loginWithGoogle,
      loginWithApple,
      socialRedirectResult,
      linkGoogleToAccount,
      linkAppleToAccount,
      linkEmailPasswordToAccount,
      linkPhoneCredentialToAccount,
      unlinkPhoneFromAccount,
      logout,
      refreshUser,
      _forceWelcomeCheck,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, authLoading, smsAuthState, socialRedirectResult]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ---------- Hook ---------- */
export const useAuth = () => useContext(AuthContext);
